/* eslint-disable react/jsx-no-target-blank */

import React, { useState } from 'react';
import './Footer.css';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import logo from "../../assets/logo-footer.png";
import IvoryLogo from "../../assets/small-brand.png";
import { useLanguage } from '../../store/language-context';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { URL } = useLanguage();
  const { t } = useTranslation();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const hiddenRoutes = ["/login", "/forget", "/otp-code", "/reset-password",
  ];

  if (hiddenRoutes.includes(location.pathname)) {
    return null;
  }

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      Swal.fire({
        icon: 'error',
        title: t('footer.email-err'),
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${URL}/subscriptions`, { email });
      if (response.status === 201) {
        // Successful subscription
        Swal.fire({
          icon: 'success',
          title: t('footer.subscription'),
          confirmButtonText: t('custom.accept'),
        });
        setEmail(''); // Clear the email input after success
      } else {
        // General error message
        Swal.fire({
          icon: 'error',
          title: t('custom.errr'),
        });
      }
    } catch (error) {
      // Check if there is a response and extract the error message
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        // If there's a specific error message, show it
        if (apiError.message) {
          Swal.fire({
            icon: 'error',
            title: apiError.message,  // Display the error message from the API
          });
        } else if (apiError.errors && apiError.errors.email) {
          // Handle specific error like email already taken
          Swal.fire({
            icon: 'error',
            title: t('footer.email-is-here'),
          });
        } else {
          // General error if no specific message is available
          Swal.fire({
            icon: 'error',
            title: t('custom.err'),
          });
        }
      } else {
        // If there's no error response (e.g., network issues), show a generic error
        Swal.fire({
          icon: 'error',
          title: t('custom.err'),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <footer className="footer mt-4 print:hidden">
        <div className="container">
          <div className="footer-container">
            {/* Logo Section */}
            <div className="flex flex-col items-start gap-4 lg:ml-8">
              <div className="logo">
                <img src={logo} alt="Logo" />
              </div>
              <p className="text-lg capitalize">broaden your success</p>
              <div className="social-icons !mt-0" dir='ltr'>
                <a href="https://www.tiktok.com/@ivorytraining?_t=8ZSERQefACw&_r=1" target='_blank'>
                  <i className="fab fa-tiktok"></i>
                </a>
                <a href="https://x.com/ivorytraining" target='_blank'>
                  <i className="fa-brands fa-x-twitter"></i>
                </a>
                <a href="https://www.instagram.com/ivorytraining/" target='_blank'>
                  <i className="fab fa-instagram"></i>
                </a>
                <a href="https://www.linkedin.com/company/ivorytraining/" target='_blank'>
                  <i className="fab fa-linkedin-in" target='_blank'></i>
                </a>


                <a href="https://www.facebook.com/ivorytraining/" target='_blank'>
                  <i className="fab fa-facebook-f"></i>
                </a>

              </div>

            </div>

            <div className="footer-section">
              <h3>{t('footer.website-map')}</h3>
              <ul>
                <li>
                  <Link to='/training-courses?page=1' className='flex items-center gap-2'>
                    <span>
                      <img src={IvoryLogo} alt='logo' />
                    </span>
                    <span>
                      {t('routes.training-courses')}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link to='/training-plan' className='flex items-center gap-2'>
                    <span>
                      <img src={IvoryLogo} alt='logo' />
                    </span>
                    <span>
                      {t('routes.training-plan')}
                    </span>
                  </Link>
                </li>

                <li>
                  <Link to='/dependencies' className='flex items-center gap-2'>
                    <span>
                      <img src={IvoryLogo} alt='logo' />
                    </span>
                    <span>
                      {t('routes.accreditations')}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link to='/podcasts' className='flex items-center gap-2'>
                    <span>
                      <img src={IvoryLogo} alt='logo' />
                    </span>
                    <span>
                      {t('routes.podcasts')}
                    </span>
                  </Link>
                </li>
              </ul>
            </div>

            <div className="footer-section">
              <h3>{t('footer.important-links')}</h3>
              <ul>
                <li>
                  <Link to='/about' className='flex items-center gap-2'>
                    <span>
                      <img src={IvoryLogo} alt='logo' />
                    </span>
                    <span>
                      {t('routes.about')}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link to='/privacy' className='flex items-center gap-2'>
                    <span>
                      <img src={IvoryLogo} alt='logo' />
                    </span>
                    <span>
                      {t('routes.privacy-policy')}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link to='/certificate-verification' className='flex items-center gap-2'>
                    <span>
                      <img src={IvoryLogo} alt='logo' />
                    </span>
                    <span>
                      {t('routes.certificate-verification')}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link to='/contactus' className='flex items-center gap-2'>
                    <span>
                      <img src={IvoryLogo} alt='logo' />
                    </span>
                    <span>
                      {t('routes.contact-us')}
                    </span>
                  </Link>
                </li>

              </ul>
            </div>

            {/* Contact Form Section */}
            <div className="footer-section contact-form">
              <h3>{t('routes.contact-us')}</h3>
              <p className='!text-start'>
                {t('footer.subs-news')}
              </p>
              <form onSubmit={handleSubmit}>
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder={t('footer.enter-email')}
                  required
                />
                <button type="submit" disabled={loading}>
                  {loading ? `${t('custom.sending')}` : t('custom.send')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </footer>

      <div className="copyright">
        <p>{t('footer.copyright')}</p>
      </div>
    </>
  );
};

export default Footer;